import { Box, DialogProps, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import encourageIllustration from '../../assets/img/mobility-survey-results-illus-2.png';
import followIllustration from '../../assets/img/mobility-survey-results-illus-3.png';
import analyseJourneysIllustration from '../../assets/img/mobility-survey-results-illus.png';
import { Button, ChevronLeftIcon, ChevronRightIcon, Dialog } from '../../components';
import useOfferDialog from '../../hooks/offer-dialog';

const steps = ['analyseJourneys', 'encourage', 'follow'] as const;

type TStep = (typeof steps)[number];

const stepsMap: {
  [key in TStep]: { descriptionKey: string; illustration: string; titleKey: string };
} = {
  analyseJourneys: {
    titleKey: 'companies.mobility_survey_dialog.analyse_journeys.title',
    descriptionKey: 'companies.mobility_survey_dialog.analyse_journeys.description',
    illustration: analyseJourneysIllustration,
  },
  encourage: {
    titleKey: 'companies.mobility_survey_dialog.encourage.title',
    descriptionKey: 'companies.mobility_survey_dialog.encourage.description',
    illustration: encourageIllustration,
  },
  follow: {
    titleKey: 'companies.mobility_survey_dialog.follow.title',
    descriptionKey: 'companies.mobility_survey_dialog.follow.description',
    illustration: followIllustration,
  },
};

function MobilitySurveyDialog({
  surveyAnswerKey,
  ...props
}: Omit<DialogProps, 'onClose'> & {
  onClose: () => void;
  surveyAnswerKey: string | null;
}): React.JSX.Element {
  const [selectedStepIndex, selectStepIndex] = useState(0);
  const { t } = useTranslation();
  useOfferDialog(props.open, surveyAnswerKey);

  const { illustration, titleKey, descriptionKey } = stepsMap[steps[selectedStepIndex]];

  return (
    <Dialog disableBackgroundClick fullWidth maxWidth="sm" {...props}>
      <Box display="flex" flexDirection="column" gap={3}>
        <img src={illustration} style={{ maxWidth: '100%' }} width={416} />
        <Typography fontSize="1.25rem" fontWeight={600}>
          {t(titleKey)}
        </Typography>
        <Typography variant="body2">{t(descriptionKey)}</Typography>
        <Box alignItems="center" display="flex" gap={3} justifyContent="flex-end">
          {selectedStepIndex > 0 && (
            <Button
              color="primary"
              onClick={() => selectStepIndex(selectedStepIndex - 1)}
              startIcon={<ChevronLeftIcon />}
              variant="text"
            >
              {t('commons.actions.prev')}
            </Button>
          )}
          {selectedStepIndex === steps.length - 1 ? (
            <Button color="primary" onClick={() => props.onClose()} variant="contained">
              {t('commons.done')}
            </Button>
          ) : (
            <Button
              color="primary"
              endIcon={<ChevronRightIcon />}
              onClick={() => selectStepIndex(selectedStepIndex + 1)}
              variant="contained"
            >
              {t('commons.actions.next')}
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

export default MobilitySurveyDialog;
